import { useState } from "react";
import { Button, Group, Stack, Text, TextInput } from "@mantine/core";
import { formatDateTime } from "@livecommerce/ui";
import { useTranslation } from "react-i18next";
import { useSession } from "../auth/useSession";
import { ApiError } from "../api/client";
import { type Show, shows } from "./api";

/**
 * The show's title and start time, editable in place (spec §8.1, `R-HOST-30`).
 *
 * ## Why the start time is editable after publication, not only in draft
 *
 * Spec §8.1 makes the case explicit: *a show may start late; the homepage must handle "scheduled
 * start time has passed but the show is not live yet"*. The remedy for a show that will now run
 * an hour later is to say so, and a start time that froze at publication would leave the panel
 * describing a show nobody is going to.
 *
 * The API takes the same view — `editShow` permits `draft`, `scheduled` and `live` — and refuses
 * an ended or cancelled show, which is where this form stops being offered.
 *
 * ## The trigger is not here
 *
 * Opening the form is a **show action**, so its button lives with the other show actions rather
 * than as a link in the date line — `ShowPage` owns the `editing` flag and `ShowActions` draws
 * the button. Splitting a component's own toggle away from it is usually a smell; here it is the
 * point. Publish, go live, end, cancel and edit are one set of things a person does to a show,
 * and a sixth control floating half an inch away in a different weight reads as a different kind
 * of thing.
 *
 * ## The value in the input is not the value in the database
 *
 * `datetime-local` speaks wall-clock with no zone, and everything is stored in UTC (arch §10).
 * So the ISO instant is converted into the browser's own zone on the way in and back out again,
 * which is the conversion a host means when they type half past seven.
 */
export function ShowDetails({
  show,
  editing,
  onChanged,
  onClose,
}: {
  show: Show;
  /** Held by `ShowPage`, because the button that sets it is in `ShowActions`. */
  editing: boolean;
  onChanged: () => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(show.title);
  const [when, setWhen] = useState(toLocalInput(show.scheduledStartAt));
  const [hostName, setHostName] = useState(show.hostName ?? "");
  const session = useSession();

  /**
   * What the show is billed as when this field is left empty.
   *
   * The signed-in account's display name, which is the right answer for the overwhelmingly common
   * case — the person editing a show is the person hosting it (`R-HOST-30` reserves both to the
   * host and to operators). An **operator** editing somebody else's show would see their own name
   * here, which would be wrong, so it falls back to a neutral phrase whenever the viewer is not the
   * host rather than confidently showing the wrong one.
   */
  const hostPlaceholder =
    session.state?.signedIn && session.state.account.id === show.hostAccountId
      ? (session.state.account.displayName ?? t("shows.hostNameFallback"))
      : t("shows.hostNameFallback");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  async function save() {
    setBusy(true);
    setError(undefined);
    try {
      await shows.edit(show.id, {
        title: title.trim(),
        // An emptied field is a start time removed, which is a real thing to want: a show whose
        // date has slipped and is not yet re-agreed is better with no time than a wrong one.
        scheduledStartAt: when ? new Date(when).toISOString() : null,
        /*
          `R-HOST-38`. Emptied means *use my display name* rather than *bill this show as nobody*,
          so a blank field is sent as `null` — the same shape the API stores.
        */
        hostName: hostName.trim() || null,
      });
      onClose();
      await onChanged();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t("shows.unreachable"));
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <Text size="xs" c="dimmed">
        {show.scheduledStartAt
          ? formatDateTime(new Date(show.scheduledStartAt))
          : t("shows.noStartTime")}
        {/*
          The billing, beside the start time, because the two are what a host sets about the show
          rather than about its contents — and because this is the only place either is visible
          without opening the editor.
        */}
        {show.hostName ? ` · ${t("shows.hostedBy", { host: show.hostName })}` : null}
      </Text>
    );
  }

  return (
    <Stack gap="xs">
      <Group gap="xs" align="flex-end" wrap="wrap">
        <TextInput
          size="xs"
          label={t("shows.titleLabel")}
          value={title}
          maxLength={120}
          onChange={(event) => setTitle(event.currentTarget.value)}
          disabled={busy}
        />
        {/*
          `R-HOST-38` — who the room says is presenting.

          The placeholder is the host account's own display name, which is what a show falls back to
          and therefore what leaving this blank produces. Showing it there rather than pre-filling
          the value is the difference between *this is what will happen* and *this is now stored on
          the show*: a host who never touches the field keeps following their account name when they
          rename themselves, and one who types here does not.
        */}
        <TextInput
          size="xs"
          label={t("shows.hostNameLabel")}
          description={t("shows.hostNameHelp")}
          placeholder={hostPlaceholder}
          value={hostName}
          maxLength={60}
          onChange={(event) => setHostName(event.currentTarget.value)}
          disabled={busy}
        />
        <TextInput
          size="xs"
          type="datetime-local"
          label={t("shows.whenLabel")}
          value={when}
          onChange={(event) => setWhen(event.currentTarget.value)}
          disabled={busy}
        />
        <Button
          size="compact-xs"
          loading={busy}
          disabled={!title.trim()}
          onClick={() => void save()}
        >
          {t("shows.saveDetails")}
        </Button>
        <Button size="compact-xs" variant="default" disabled={busy} onClick={onClose}>
          {t("shows.cancelEdit")}
        </Button>
      </Group>
      {error ? (
        <Text size="xs" c="red">
          {error}
        </Text>
      ) : null}
    </Stack>
  );
}

/**
 * An ISO instant as `datetime-local` wants it: `YYYY-MM-DDTHH:mm`, in the browser's own zone.
 *
 * Built by subtracting the offset and slicing the ISO string rather than by assembling the parts
 * by hand, because the manual version needs four `padStart`s and gets one of them wrong.
 */
function toLocalInput(instant: string | null): string {
  if (!instant) return "";
  const when = new Date(instant);
  const local = new Date(when.getTime() - when.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}
